import { toast } from "sonner";
import { darfJetzt, useUserContext } from "@/auth/operations";
import { kannFreigabeSchalten, kannOwnerFreigabeSchalten } from "./pluginGate";
import { TableCell, TableHead } from "@/components/ui/table";
import type { Plugin } from "@/types";
import { useSetPluginApproved, useSetPluginOwnerAvailable } from "@/api/mutations/plugins";

// #288 (UC-6): Die zwei Entscheidungen an einem Plugin — an EINER Stelle, weil Katalog-Liste
// und Marketplace-Detail dieselbe Tabelle zeigen. Zwei Kopien hätten hier bedeutet: zwei
// Stellen, an denen man die Regel „anbieten setzt freigeben voraus" vergessen kann.
// #290 (UC-3): die Schalter selbst sind Table-frei (`…Toggle`) — das Plugin-Detail zeigt
// DIESELBEN Bedienelemente außerhalb einer Tabelle; die Cells wrappen sie nur.
//
//   Freigeschaltet     — „diesem Plugin vertrauen wir" (#288 approved). Ohne sie NIRGENDS zuweisbar.
//   Owner-Self-Service — „das darf der Agent-Owner selbst schalten". Teilmenge davon.
//
// Vokabular-Systematik (#291, Arbens Live-Fund „pur chaos"): „freiGEBEN" = einen ENTWURF
// mergen (Lifecycle), „freiSCHALTEN" = Plattform-Vertrauen (#288) — nie mischen.

export function PluginDecisionHeads() {
  return (
    <>
      <TableHead>Freigeschaltet</TableHead>
      {/* Arbens Live-Fund: „Owner-Freigabe" kollidierte begrifflich mit der
          Artefakt-Ownership (#273) — gemeint ist der Self-Service des AGENT-Owners. */}
      <TableHead title="Dürfen Agent-Owner dieses Plugin selbst für ihre Agents aktivieren? (Teilmenge des Freigeschalteten)">
        Owner-Self-Service
      </TableHead>
    </>
  );
}

export function PluginApprovedToggle({ p }: { p: Plugin }) {
  // #295: Freischalten = Plattform-Vertrauen — fuer Nicht-Admins (author) deaktiviert.
  // #299: nicht mehr ueber den Rollen-String — der Server sagt, ob dieser Mensch
  // freischalten darf (Plattform-Vertrauen, #288). Die Sperre selbst bleibt serverseitig.
  // R2 L-E: waehrend des Ladens gesperrt (kein Flackern in ein 403), bei einem
  // FEHLGESCHLAGENEN Kontext-Abruf wieder offen — sonst sperrte ein aelterer Master oder
  // ein Netzfehler den Admin dauerhaft aus seinem eigenen Schalter aus.
  const nichtAdmin = !darfJetzt(useUserContext(), "plugin.approve");
  const setApproved = useSetPluginApproved();
  return (
    <label
      className="flex cursor-pointer items-center gap-2"
      title={
        p.approved
          ? "Freigeschaltet — zuweisbar und wird ausgeliefert. Wegnehmen wirkt sofort (kein Entwurf, keine Git-Änderung nötig)."
          : "Nicht freigeschaltet — dieses Plugin lässt sich keinem Agent zuweisen und wird nirgends ausgeliefert."
      }
    >
      <input
        type="checkbox"
        checked={p.approved}
        // Freigeben geht nur gegen ein gelesenes Manifest — Sperren dagegen IMMER, auch
        // bei verwaistem Eintrag oder toter Quelle (das ist der Not-Aus).
        disabled={nichtAdmin || setApproved.isPending || !kannFreigabeSchalten(p)}
        onChange={(e) => {
          // Arbens Live-Fund: `e.target.checked` NICHT im onSuccess erneut aus dem DOM
          // lesen — bis der Server antwortet, kann React die controlled Checkbox längst
          // zurückgesetzt haben, und der Toast meldete das Gegenteil der Aktion.
          const neu = e.target.checked;
          setApproved.mutate(
            { marketplaceId: p.marketplaceId, pluginName: p.name, approved: neu },
            {
              onSuccess: () =>
                toast.success(`${p.name}: ${neu ? "freigeschaltet" : "gesperrt — wirkt beim nächsten Push"}`),
              onError: (err) => toast.error(`Fehlgeschlagen: ${(err as Error).message}`),
            },
          );
        }}
        data-testid={`plugin-approved-toggle-${p.marketplaceId}-${p.name}`}
      />
      <span className={p.approved ? "text-[12px] text-ok-text" : "text-[12px] text-warn"}>
        {p.approved ? "freigeschaltet" : "nicht freigeschaltet"}
      </span>
    </label>
  );
}

export function PluginOwnerToggle({ p }: { p: Plugin }) {
  // #299: nicht mehr ueber den Rollen-String — der Server sagt, ob dieser Mensch
  // freischalten darf (Plattform-Vertrauen, #288). Die Sperre selbst bleibt serverseitig.
  // R2 L-E: siehe oben — laden = gesperrt, Abruf-Fehler = wieder offen.
  const nichtAdmin = !darfJetzt(useUserContext(), "plugin.approve");
  const setOwnerAvailable = useSetPluginOwnerAvailable();
  return (
    <label
      className="flex cursor-pointer items-center gap-2"
      title={
        p.approved
          ? "Plugin dem Owner zum Self-Service anbieten"
          : "Erst freischalten — angeboten werden kann nur, was freigeschaltet ist"
      }
    >
      <input
        type="checkbox"
        checked={p.ownerAvailable}
        // Ohne Plattform-Freigabe gibt es nichts anzubieten (die Invariante steht in der
        // Domäne; hier wird sie nur sichtbar, statt den Nutzer in einen 403 laufen zu lassen).
        disabled={nichtAdmin || setOwnerAvailable.isPending || !kannOwnerFreigabeSchalten(p)}
        onChange={(e) => {
          const neu = e.target.checked; // s.o. — vor dem Roundtrip einfrieren
          setOwnerAvailable.mutate(
            { marketplaceId: p.marketplaceId, pluginName: p.name, ownerAvailable: neu },
            {
              onSuccess: () => toast.success(`${p.name}: Self-Service für Agent-Owner ${neu ? "an" : "aus"}`),
              onError: (err) => toast.error(`Fehlgeschlagen: ${(err as Error).message}`),
            },
          );
        }}
        data-testid={`plugin-owner-toggle-${p.marketplaceId}-${p.name}`}
      />
      <span className="text-[12px] text-text3">{p.ownerAvailable ? "angeboten" : "nicht angeboten"}</span>
    </label>
  );
}

export function PluginDecisionCells({ p }: { p: Plugin }) {
  return (
    <>
      <TableCell>
        <PluginApprovedToggle p={p} />
      </TableCell>
      <TableCell>
        <PluginOwnerToggle p={p} />
      </TableCell>
    </>
  );
}
